/**
 * Carousel configuration constants for different carousel types
 * Used by useVirtualizedCarousel hook for reusable carousel logic
 */

export interface CarouselConfig {
    SLIDE_ROW_GAP: number;
    SLIDE_ROW_GAP_VERTICAL: number;
    SLIDE_COLUMN_GAP: number;
    SLIDE_COLUMN_GAP_VERTICAL: number;
    SLIDE_ROW_GAP_VERTICAL_HALF?: number;
    SLIDE_COLUMN_GAP_VERTICAL_HALF?: number;
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: number;
        HORIZONTAL_HALF_SCREEN: number;
        VERTICAL: number;
        VERTICAL_HALF_SCREEN: number;
    };
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: number;
        HORIZONTAL_HALF_SCREEN: number;
        VERTICAL: number;
        VERTICAL_HALF_SCREEN: number;
    };
    NAVIGATION_BUTTON_OFFSET: number;
    CONTAINER_PADDING: number;
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
        VERTICAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
    };
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
        VERTICAL: {
            FULL_SCREEN: number;
            HALF_SCREEN: number;
        };
    };
    VIRTUAL_CACHE_SIZE: number;
    PRELOAD_SLIDES: number;
}

// Shop carousel configuration (2×4 grid fullscreen; widths chosen to match prior total swiper width 3×424+2×24 ≈ 4×312+3×24)
export const SHOP_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_ROW_GAP_VERTICAL: 16,
    SLIDE_COLUMN_GAP: 12,
    SLIDE_COLUMN_GAP_VERTICAL: 12,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 450,
        HORIZONTAL_HALF_SCREEN: 450,
        VERTICAL: 502,
        VERTICAL_HALF_SCREEN: 502,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 160,
        HORIZONTAL_HALF_SCREEN: 160,
        VERTICAL: 180,
        VERTICAL_HALF_SCREEN: 130,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 4,
        },
        VERTICAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 2,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 5,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// FnB carousel configuration
export const FNB_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_ROW_GAP_VERTICAL: 16,
    SLIDE_COLUMN_GAP: 24,
    SLIDE_COLUMN_GAP_VERTICAL: 24,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 424,
        HORIZONTAL_HALF_SCREEN: 424,
        VERTICAL: 424,
        VERTICAL_HALF_SCREEN: 424,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 148,
        HORIZONTAL_HALF_SCREEN: 148,
        VERTICAL: 148,
        VERTICAL_HALF_SCREEN: 114,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 3,
        },
        VERTICAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 2,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 2,
        },
        VERTICAL: {
            FULL_SCREEN: 6,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Facility carousel configuration
export const FACILITY_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_ROW_GAP_VERTICAL: 16,
    SLIDE_COLUMN_GAP: 16,
    SLIDE_COLUMN_GAP_VERTICAL: 16,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 200,
        HORIZONTAL_HALF_SCREEN: 200,
        VERTICAL: 200,
        VERTICAL_HALF_SCREEN: 200,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 214,
        HORIZONTAL_HALF_SCREEN: 214,
        VERTICAL: 214,
        VERTICAL_HALF_SCREEN: 124,
    },
    NAVIGATION_BUTTON_OFFSET: 100,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 5,
            HALF_SCREEN: 5,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 3,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Facility category carousel configuration (FI-01-03: 220×266 cards, 40px gaps, 5×2 horizontal / 2×5 vertical; half-screen horizontal: 220×178, 5×2)
export const FACILITY_CATEGORY_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 40,
    SLIDE_ROW_GAP_VERTICAL: 40,
    SLIDE_COLUMN_GAP: 40,
    SLIDE_COLUMN_GAP_VERTICAL: 40,
    SLIDE_ROW_GAP_VERTICAL_HALF: 24,
    SLIDE_COLUMN_GAP_VERTICAL_HALF: 24,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 220,
        HORIZONTAL_HALF_SCREEN: 220,
        VERTICAL: 220,
        VERTICAL_HALF_SCREEN: 220,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 266,
        HORIZONTAL_HALF_SCREEN: 178,
        VERTICAL: 266,
        VERTICAL_HALF_SCREEN: 178,
    },
    NAVIGATION_BUTTON_OFFSET: 100,
    CONTAINER_PADDING: 0,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 5,
            HALF_SCREEN: 5,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 3,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 2,
        },
        VERTICAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Default carousel configuration - can be used as base for new carousels
export const DEFAULT_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 16,
    SLIDE_ROW_GAP_VERTICAL: 16,
    SLIDE_COLUMN_GAP: 20,
    SLIDE_COLUMN_GAP_VERTICAL: 20,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 300,
        HORIZONTAL_HALF_SCREEN: 300,
        VERTICAL: 300,
        VERTICAL_HALF_SCREEN: 300,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 180,
        HORIZONTAL_HALF_SCREEN: 180,
        VERTICAL: 180,
        VERTICAL_HALF_SCREEN: 140,
    },
    NAVIGATION_BUTTON_OFFSET: 80,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 4,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 3,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 2,
        },
    },
    VIRTUAL_CACHE_SIZE: 25,
    PRELOAD_SLIDES: 2,
} as const;

export const LIST_TYPE_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 18,
    SLIDE_ROW_GAP_VERTICAL: 12,
    SLIDE_COLUMN_GAP: 20,
    SLIDE_COLUMN_GAP_VERTICAL: 40,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 540,
        HORIZONTAL_HALF_SCREEN: 540,
        VERTICAL: 380,
        VERTICAL_HALF_SCREEN: 380,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 280,
        HORIZONTAL_HALF_SCREEN: 280,
        VERTICAL: 436,
        VERTICAL_HALF_SCREEN: 436,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 3,
        },
        VERTICAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 2,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 3,
            HALF_SCREEN: 1,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

export const CARD_TYPE_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 0,
    SLIDE_ROW_GAP_VERTICAL: 0,
    SLIDE_COLUMN_GAP: 0,
    SLIDE_COLUMN_GAP_VERTICAL: 0,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 1312,
        HORIZONTAL_HALF_SCREEN: 1047,
        VERTICAL: 820,
        VERTICAL_HALF_SCREEN: 1020,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 580,
        HORIZONTAL_HALF_SCREEN: 396,
        VERTICAL: 1290,
        VERTICAL_HALF_SCREEN: 517,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 0,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 1,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 1,
            HALF_SCREEN: 1,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 1,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 1,
            HALF_SCREEN: 1,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

export const EVENT_CAROUSEL_CONFIG: CarouselConfig = {
    SLIDE_ROW_GAP: 18,
    SLIDE_ROW_GAP_VERTICAL: 40,
    SLIDE_COLUMN_GAP: 40,
    SLIDE_COLUMN_GAP_VERTICAL: 40,
    SLIDE_ITEM_WIDTH: {
        HORIZONTAL: 339,
        HORIZONTAL_HALF_SCREEN: 366,
        VERTICAL: 339,
        VERTICAL_HALF_SCREEN: 339,
    },
    SLIDE_ITEM_HEIGHT: {
        HORIZONTAL: 578,
        HORIZONTAL_HALF_SCREEN: 408,
        VERTICAL: 578,
        VERTICAL_HALF_SCREEN: 578,
    },
    NAVIGATION_BUTTON_OFFSET: 60,
    CONTAINER_PADDING: 40,
    ITEMS_PER_ROW: {
        HORIZONTAL: {
            FULL_SCREEN: 4,
            HALF_SCREEN: 4,
        },
        VERTICAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 2,
        },
    },
    ROWS_PER_SLIDE: {
        HORIZONTAL: {
            FULL_SCREEN: 1,
            HALF_SCREEN: 1,
        },
        VERTICAL: {
            FULL_SCREEN: 2,
            HALF_SCREEN: 1,
        },
    },
    VIRTUAL_CACHE_SIZE: 100,
    PRELOAD_SLIDES: 2,
} as const;

// Carousel type enum for easy identification
export enum CarouselType {
    SHOP = 'shop',
    FNB = 'fnb',
    FACILITY = 'facility',
    FACILITY_CATEGORY = 'facility_category',
    LIST_TYPE = 'list_type',
    CARD_TYPE = 'card_type',
    CATEGORY_TYPE = 'category_type',
    DEFAULT = 'default',
    EVENT = 'event',
}

export const CATEGORY_TYPE_CAROUSEL_CONFIG: CarouselConfig = SHOP_CAROUSEL_CONFIG;

// Helper function to get carousel config by type
export const getCarouselConfig = (type: CarouselType): CarouselConfig => {
    switch (type) {
        case CarouselType.SHOP:
            return SHOP_CAROUSEL_CONFIG;
        case CarouselType.FNB:
            return FNB_CAROUSEL_CONFIG;
        case CarouselType.FACILITY:
            return FACILITY_CAROUSEL_CONFIG;
        case CarouselType.FACILITY_CATEGORY:
            return FACILITY_CATEGORY_CAROUSEL_CONFIG;
        case CarouselType.LIST_TYPE:
            return LIST_TYPE_CAROUSEL_CONFIG;
        case CarouselType.CARD_TYPE:
            return CARD_TYPE_CAROUSEL_CONFIG;
        case CarouselType.CATEGORY_TYPE:
            return CATEGORY_TYPE_CAROUSEL_CONFIG;
        case CarouselType.EVENT:
            return EVENT_CAROUSEL_CONFIG;
        case CarouselType.DEFAULT:
        default:
            return DEFAULT_CAROUSEL_CONFIG;
    }
};
